import { useCallback, useEffect, useState } from 'react';
import { Bell } from 'lucide-react';
import { apiFetch, getAccessToken, v1Path } from '../../lib/api';
import { formatCommentWhen } from '../../lib/comments';
import { cn } from '@/lib/utils';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { useSession } from '../session-context';
import { useOverlays } from './overlay-context';
import Peek from './Peek';

type Notice = {
  id: string;
  kind: string;
  message: string;
  read_at: string | null;
  created_at: string;
  work_item: { id: string; key: string; title: string; project_id: string } | null;
};

type Page = { unread: number; items: Notice[] };

/** One open connection for the signed-in person. Page changes do not start a new check. */
let liveFor: string | null = null;
let cached: Page | null = null;
let streamAbort: AbortController | null = null;
const listeners = new Set<(page: Page) => void>();

function show(userId: string, page: Page) {
  if (liveFor !== userId) return;
  cached = page;
  Array.from(listeners).forEach((listener) => listener(page));
}

async function readNotices(userId: string) {
  try {
    const next = await apiFetch<Page>(v1Path('/notifications'));
    show(userId, next);
  } catch {
    // A failed read leaves the last list on screen.
  }
}

function watchNotices(userId: string) {
  if (liveFor === userId && streamAbort) return;
  streamAbort?.abort();
  liveFor = userId;
  cached = null;
  const controller = new AbortController();
  streamAbort = controller;

  const pause = (ms: number) => new Promise<void>((resolve) => {
    const timer = window.setTimeout(resolve, ms);
    controller.signal.addEventListener('abort', () => {
      window.clearTimeout(timer);
      resolve();
    }, { once: true });
  });

  void (async () => {
    let delay = 1000;
    while (!controller.signal.aborted && liveFor === userId) {
      try {
        const token = getAccessToken();
        const headers = new Headers({ Accept: 'text/event-stream' });
        if (token) headers.set('Authorization', `Bearer ${token}`);
        const res = await fetch(v1Path('/notifications/stream'), { headers, signal: controller.signal });
        if (controller.signal.aborted || liveFor !== userId) return;
        if (res.status === 401) {
          await readNotices(userId);
        } else if (res.ok && res.body) {
          delay = 1000;
          const reader = res.body.getReader();
          const decoder = new TextDecoder();
          let buffer = '';
          let caughtUp = false;
          while (!controller.signal.aborted && liveFor === userId) {
            const { done, value } = await reader.read();
            if (done) break;
            buffer += decoder.decode(value, { stream: true });
            let end = buffer.indexOf('\n\n');
            while (end !== -1) {
              const frame = buffer.slice(0, end);
              buffer = buffer.slice(end + 2);
              const lines = frame.split('\n');
              const woke = lines.some((line) => line.startsWith('data:'));
              const opened = lines.some((line) => line.startsWith(': ok'));
              if (woke || (opened && !caughtUp)) {
                caughtUp = true;
                void readNotices(userId);
              }
              end = buffer.indexOf('\n\n');
            }
          }
        }
      } catch {
        if (controller.signal.aborted || liveFor !== userId) return;
      }
      if (controller.signal.aborted || liveFor !== userId) return;
      await pause(delay);
      delay = Math.min(delay * 2, 30_000);
    }
  })();
}

export default function NotificationsBell() {
  const { userId, viewAs } = useSession();
  const { toast } = useOverlays();
  const [open, setOpen] = useState(false);
  const [page, setPage] = useState<Page | null>(userId && liveFor === userId ? cached : null);
  const [pinnedKey, setPinnedKey] = useState<string | null>(null);

  const refresh = useCallback(() => {
    if (!userId) return;
    void readNotices(userId);
  }, [userId]);

  useEffect(() => {
    if (!userId) return undefined;
    watchNotices(userId);
    setPage(liveFor === userId ? cached : null);
    const listener = (next: Page) => setPage(next);
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  }, [userId]);

  async function mark(ids: string[] | 'all') {
    if (viewAs) return;
    try {
      const body = ids === 'all' ? { all: true } : { ids };
      const result = await apiFetch<{ unread: number }>(v1Path('/notifications/read'), {
        method: 'POST',
        body: JSON.stringify(body),
      });
      const now = new Date().toISOString();
      setPage((current) => {
        if (!current) return current;
        const next = {
          unread: result.unread,
          items: current.items.map((item) => (
            ids === 'all' || ids.includes(item.id) ? { ...item, read_at: item.read_at ?? now } : item
          )),
        };
        if (userId && liveFor === userId) cached = next;
        return next;
      });
    } catch {
      toast('Could not update notifications');
    }
  }

  const unread = page?.unread ?? 0;
  const label = unread > 0 ? `Notifications, ${unread} unread` : 'Notifications';

  function openNotice(item: Notice) {
    const key = item.work_item?.key ?? null;
    setOpen(false);
    if (!item.read_at) void mark([item.id]);
    if (!key) return;
    // Wait until the menu has closed, so this click is not treated as a click outside the popup.
    window.setTimeout(() => setPinnedKey(key), 0);
  }

  return (
    <>
    <Popover open={open} onOpenChange={(next) => { setOpen(next); if (next) refresh(); }}>
      <PopoverTrigger asChild>
        <Button type="button" size="icon" variant="ghost" className="relative" aria-label={label}>
          <Bell className="h-4 w-4" aria-hidden />
          {unread > 0 ? (
            <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold leading-none text-primary-foreground">
              {unread > 9 ? '9+' : unread}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 p-0">
        <div className="flex items-center justify-between gap-3 border-b px-3 py-2">
          <p className="text-sm font-medium">Notifications</p>
          {unread > 0 && !viewAs ? (
            <Button type="button" variant="link" className="h-auto px-0 text-xs" onClick={() => { void mark('all'); }}>
              Mark all read
            </Button>
          ) : null}
        </div>
        {viewAs ? (
          <p className="border-b px-3 py-2 text-xs text-muted-foreground">You're viewing Pulse as {viewAs.name}.</p>
        ) : null}
        {page == null ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">Loading…</p>
        ) : page.items.length === 0 ? (
          <p className="px-3 py-6 text-center text-sm text-muted-foreground">You're up to date.</p>
        ) : (
          <ul className="max-h-96 overflow-y-auto">
            {page.items.map((item) => {
              const when = formatCommentWhen(item.created_at);
              const read = Boolean(item.read_at);
              const body = (
                <>
                  <span
                    className={cn(
                      'h-2.5 w-2.5 shrink-0 rounded-full border-2 border-blue-600',
                      read ? 'bg-transparent' : 'bg-blue-600 shadow-[0_0_8px_2px_rgba(37,99,235,0.45)]',
                    )}
                    aria-hidden
                  />
                  <span className="min-w-0">
                    <span className="sr-only">{read ? 'Read. ' : 'Unread. '}</span>
                    <p className={cn('text-sm text-pretty', read ? 'text-muted-foreground' : 'text-foreground')}>{item.message}</p>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {item.work_item ? `${item.work_item.key} · ${when}` : when}
                    </p>
                  </span>
                </>
              );
              const className = cn(
                'flex items-center gap-2.5 px-3 py-2.5 hover:bg-accent',
                !read && 'bg-accent/40',
              );
              return (
                <li key={item.id}>
                  {item.work_item ? (
                    <button type="button" className={cn(className, 'w-full text-left')} onClick={() => openNotice(item)}>
                      {body}
                    </button>
                  ) : (
                    <div className={className}>{body}</div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </PopoverContent>
    </Popover>
    <Peek pinnedKey={pinnedKey} onPinnedClose={() => setPinnedKey(null)} />
    </>
  );
}
